import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CardLoadingSpinner } from '@/components/ui/loading-spinner';
import { Class } from '@/types';
import { classApi } from '@/lib/api';
import StudentAssignment from '@/components/StudentAssignment';

const ManageClassStudentsPage = () => {
  const { classId } = useParams<{ classId: string }>();
  const [classData, setClassData] = useState<Class | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchClassData();
  }, [classId, toast]);

  const fetchClassData = async () => {
    try {
      setLoading(true);
      if (classId) {
        const data = await classApi.getById(classId);
        setClassData(data);
      }
    } catch (error) {
      console.error('Error fetching class data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load class data. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentChange = (studentIds: string[]) => {
    if (classData) {
      setClassData({
        ...classData,
        studentIds
      });
    }
  };

  const handleAssignmentSaved = () => {
    // Refetch class data to get updated studentCount
    fetchClassData();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to={`/classes/${classId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Manage Class Students</h1>
      </div>

      {loading ? (
        <Card>
          <CardContent>
            <CardLoadingSpinner message="Loading class data..." />
          </CardContent>
        </Card>
      ) : classData ? (
        <Card>
          <CardHeader>
            <CardTitle>Assign Students to {classData.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <StudentAssignment
              classId={classId || ''}
              batchId={classData.batchId || classData.academicYearId || ''}
              initialStudentIds={classData.studentIds}
              onAssignmentChange={handleAssignmentChange}
              onAssignmentSaved={handleAssignmentSaved}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <p className="text-muted-foreground">Class not found.</p>
              <Button className="mt-4" asChild>
                <Link to="/classes">Back to Classes</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ManageClassStudentsPage;
